import React, { useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const DNAME = process.env.REACT_APP_DNAME;

function Login({ update }) {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [data, setData] = useState({ name: '', password: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await axios.post(`${DNAME}/login`, data);

            // Handle NEW backend (JWT): response is { token, name }
            // Handle OLD backend (no JWT): response is the user object directly
            const hasToken = result.data && result.data.token;
            const isOldSuccess = result.data && result.data._id; // old backend returns user object

            if (hasToken) {
                // New backend with JWT” store token
                localStorage.setItem('token', result.data.token);
                localStorage.setItem('loggedIn', true);
                toast.success('Logged in successfully!');
                navigate('/admin/home');
                update();
                setLoading(false);
            } else if (isOldSuccess) {
                // Old backend (production not yet updated)” still allow login
                localStorage.setItem('loggedIn', true);
                toast.success('Logged in successfully!');
                navigate('/admin/home');
                update();
                setLoading(false);
            } else {
                setLoading(false);
                toast.error('Enter valid details');
            }
        } catch (error) {
            setLoading(false);
            // 401 from server = wrong credentials, anything else = server error
            if (error.response && error.response.status === 401) {
                toast.error('Invalid credentials. Please try again.');
            } else {
                toast.error('Server error. Please try again.');
            }
        }
    };

    return (
        <>
            <div className="login-bg">
                {/* Animated floating orbs */}
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="orb orb-3" />

                <div className="login-card">
                    {/* Icon */}
                    <div className="login-icon-wrap">
                        <div className="login-icon">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 11H7V8a5 5 0 0110 0v3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <rect x="3" y="11" width="18" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
                                <circle cx="12" cy="16.5" r="1.5" fill="currentColor" />
                            </svg>
                        </div>
                    </div>

                    <h1 className="login-title">Admin Portal</h1>
                    <p className="login-subtitle">Satta Bazar Dashboard</p>

                    <form onSubmit={handleSubmit} className="login-form">
                        {/* Username */}
                        <div className="input-group">
                            <span className="input-icon">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                                    <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                            <input
                                name="name"
                                type="text"
                                id="admin-username"
                                value={data.name}
                                onChange={handleChange}
                                placeholder="Username"
                                className="login-input"
                                required
                                autoComplete="username"
                            />
                        </div>

                        {/* Password */}
                        <div className="input-group">
                            <span className="input-icon">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="11" width="18" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
                                    <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                            <input
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                id="admin-password"
                                value={data.password}
                                onChange={handleChange}
                                placeholder="Password"
                                className="login-input"
                                required
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="eye-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? (
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M10.73 10.73A3 3 0 0013.27 13.27" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="2" />
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        <button
                            type="submit"
                            id="admin-login-btn"
                            className={`login-btn ${loading ? 'login-btn--loading' : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner" />
                            ) : (
                                <>
                                    <span>Sign In</span>
                                    <svg viewBox="0 0 24 24" fill="none" className="btn-arrow">
                                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
}

export default Login;
